import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

// The Tickets workflow only deals with stories in this status.
const STATUS_FILTER = "Testing";

/**
 * Optional "link this test case to a Jira ticket in Testing status" control for
 * the normal create-test-case flows.
 *
 * When the user opts in, they pick Project -> Epic -> Testing ticket. Selecting
 * a ticket ensures a local ticket anchor exists (POST /api/ticket-links, keyed
 * by the Jira key and tied to `boardId`) and reports the anchor's ticketkey up
 * via onChange. The parent then sends those keys as `link_ticket_keys` when it
 * POSTs the new test case, so the join rows are created server-side.
 *
 * Props:
 *   boardId  - board the new test case belongs to (the ticket is tied to it too)
 *   userkey  - current user, stamped on any anchor we create
 *   value    - array of selected { ticketkey, ticket_key, summary }
 *   onChange - (nextValue) => void
 */
export default function TicketLinkField({ boardId, userkey, value = [], onChange, locked = false }) {
  const [enabled, setEnabled] = useState(false);

  const [connected, setConnected] = useState(null); // null=unknown
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [epics, setEpics] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [linking, setLinking] = useState("");

  const selectedKeys = useMemo(
    () => new Set((value || []).map((v) => v.ticketkey)),
    [value]
  );

  // Lazily check connection + load projects the first time the user opts in.
  const bootstrap = useCallback(async () => {
    setError("");
    try {
      const sres = await fetch(`${API_BASE_URL}jira/status`);
      const sbody = await sres.json().catch(() => ({}));
      const ok = !!sbody?.data?.connected;
      setConnected(ok);
      if (!ok) {
        setError(sbody?.data?.email || "Not connected to Jira.");
        return;
      }
      const pres = await fetch(`${API_BASE_URL}jira/projects`);
      const pbody = await pres.json().catch(() => ({}));
      const list = Array.isArray(pbody?.data) ? pbody.data : [];
      setProjects(list);
      if (list.length) setSelectedProject((cur) => cur || list[0].key);
    } catch {
      setConnected(false);
      setError("Network error while contacting Jira.");
    }
  }, []);

  useEffect(() => {
    if (enabled && connected === null) bootstrap();
  }, [enabled, connected, bootstrap]);

  // Load epics when the project changes.
  useEffect(() => {
    if (!enabled || !connected || !selectedProject) return;
    let active = true;
    setSelectedEpic("");
    setTickets([]);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}jira/epics?project=${encodeURIComponent(selectedProject)}`
        );
        const body = await res.json().catch(() => ({}));
        if (active) setEpics(Array.isArray(body?.data) ? body.data : []);
      } catch {
        if (active) setEpics([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [enabled, connected, selectedProject]);

  // Load Testing tickets when the epic changes.
  useEffect(() => {
    if (!enabled || !connected || !selectedProject || !selectedEpic) {
      setTickets([]);
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}jira/tickets?project=${encodeURIComponent(selectedProject)}` +
            `&epic=${encodeURIComponent(selectedEpic)}` +
            `&status=${encodeURIComponent(STATUS_FILTER)}&max_results=100`
        );
        const body = await res.json().catch(() => ({}));
        if (active) setTickets(Array.isArray(body?.data?.issues) ? body.data.issues : []);
      } catch {
        if (active) setTickets([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [enabled, connected, selectedProject, selectedEpic]);

  // Ensure the local anchor for a chosen Jira ticket, then add it to selection.
  const addTicket = async (t) => {
    setError("");
    setLinking(t.key);
    try {
      const res = await fetch(`${API_BASE_URL}ticket-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket_key: t.key,
          project_key: selectedProject,
          epic_key: t.epic_key || selectedEpic,
          summary: t.summary || "",
          status: t.status || STATUS_FILTER,
          issue_type: t.issue_type || "",
          priority: t.priority || "",
          url: t.url || "",
          board_id: boardId || null,
          userkey: userkey || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.ticketkey) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const anchor = {
        ticketkey: body.data.ticketkey,
        ticket_key: body.data.ticket_key,
        summary: body.data.summary || t.summary || "",
      };
      if (!selectedKeys.has(anchor.ticketkey)) {
        onChange?.([...(value || []), anchor]);
      }
    } catch (e) {
      setError(`Could not link ${t.key}: ${e.message}`);
    } finally {
      setLinking("");
    }
  };

  const removeTicket = (ticketkey) => {
    onChange?.((value || []).filter((v) => v.ticketkey !== ticketkey));
  };

  // Locked mode: the ticket is fixed by the caller (e.g. launched from the
  // Tickets page "Create Tests" flow). Show the pre-linked ticket(s) read-only;
  // every created case auto-links to them.
  if (locked) {
    return (
      <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4">
        <p className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Icons.Boards className="h-4 w-4 text-brand-600" />
          These test case(s) will be linked to:
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {(value || []).length ? (
            value.map((v) => (
              <span
                key={v.ticketkey}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/20"
              >
                <span className="font-mono">{v.ticket_key}</span>
                {v.summary ? (
                  <span className="max-w-[18rem] truncate text-slate-500">{v.summary}</span>
                ) : null}
              </span>
            ))
          ) : (
            <span className="text-xs text-slate-400">No ticket selected.</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        Link this test case to a Jira ticket in{" "}
        <span className="font-semibold text-blue-700">{STATUS_FILTER}</span> status?
      </label>

      {/* Chosen tickets */}
      {(value || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((v) => (
            <span
              key={v.ticketkey}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/20"
            >
              <span className="font-mono">{v.ticket_key}</span>
              <button
                type="button"
                onClick={() => removeTicket(v.ticketkey)}
                className="text-brand-500 hover:text-brand-800"
                aria-label={`Unlink ${v.ticket_key}`}
              >
                <Icons.X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {enabled && (
        <div className="mt-3 space-y-3">
          {connected === false ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error || "Not connected to Jira."}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={!projects.length}
                  className="min-w-[12rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                >
                  {!projects.length && <option>Loading projects…</option>}
                  {projects.map((p) => (
                    <option key={p.id || p.key} value={p.key}>
                      {p.name} ({p.key})
                    </option>
                  ))}
                </select>

                <select
                  value={selectedEpic}
                  onChange={(e) => setSelectedEpic(e.target.value)}
                  disabled={!epics.length}
                  className="min-w-[16rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                >
                  <option value="">
                    {epics.length ? "Select an epic…" : "No epics"}
                  </option>
                  {epics.map((ep) => (
                    <option key={ep.key} value={ep.key}>
                      {ep.key} — {ep.summary}
                    </option>
                  ))}
                </select>
              </div>

              {error && connected !== false && (
                <p className="text-sm text-red-600">{error}</p>
              )}

              {selectedEpic && (
                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                  {loading ? (
                    <div className="flex items-center gap-2 px-3 py-4 text-sm text-slate-500">
                      <Icons.Spinner className="h-4 w-4 animate-spin" />
                      Loading {STATUS_FILTER} tickets…
                    </div>
                  ) : !tickets.length ? (
                    <div className="px-3 py-4 text-center text-sm text-slate-400">
                      No {STATUS_FILTER} tickets under this epic.
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {tickets.map((t) => {
                        const already = (value || []).some((v) => v.ticket_key === t.key);
                        return (
                          <li
                            key={t.key}
                            className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                          >
                            <span className="min-w-0">
                              <span className="font-mono font-medium text-brand-700">
                                {t.key}
                              </span>{" "}
                              <span className="text-slate-700">{t.summary}</span>
                            </span>
                            <button
                              type="button"
                              disabled={already || linking === t.key}
                              onClick={() => addTicket(t)}
                              className="flex-none rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
                            >
                              {already ? "Linked" : linking === t.key ? "Linking…" : "Link"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
